import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonTable from "../../../../components/Table/Table";
import "./CategoryList.css";
import AddCategory from "../AddCategory/AddCategory";
import { environment } from "../../../../environments/environment";
import {
  getCategories,
  updateCategoryStatusApi,
  deleteCategoryApi,
  approveCategoryApi
} from "../../../../services/Category/category.service";
import { Category, CategoryStatus } from "../../../../services/Category/category.types";
import { toast } from "react-toastify";
import { Pencil, Eye, Trash2 } from "lucide-react";
// import { CategoryStatus } from "../../../../services/Category/category.types";

const CategoryList: React.FC = () => {
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();
  const calledOnce = useRef(false);

  const loadCategories = async () => {
    try {
      const data = await getCategories();

      if (Array.isArray(data)) {
        const mapped: Category[] = data.map((c: any) => ({
          id: c.id,

          name: c.name,

          metalTypeId: c.metalTypeId,

          metalTypeName: c.Metal_type?.name || "N/A",

          createdByRole: c.createdByRole, // ✅ ADD THIS
          description: c.description,

          image: (() => {
            const raw = c.image || c.image_url || c.imageUrl || null;

            if (!raw || typeof raw !== "string") return null;

            return raw.startsWith("http")
              ? raw
              : `${environment.fileUrl}/${raw.replace(/^\/+/, "")}`;
          })(),

          categortystatus:
            (c.categortystatus as CategoryStatus) ||
            (c.status?.toUpperCase() === "ACTIVE"
              ? CategoryStatus.ACTIVE
              : CategoryStatus.INACTIVE),

           status: c.status,
        }));

        setCategories(mapped);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching:", error);
      setCategories([]);
    }
  };

  useEffect(() => {
    if (calledOnce.current) return;
    calledOnce.current = true;
    loadCategories();
  }, []);



  const toggleStatus = async (id: number, currentStatus: CategoryStatus) => {
    const newStatus =
      currentStatus === CategoryStatus.ACTIVE
        ? CategoryStatus.INACTIVE
        : CategoryStatus.ACTIVE;

    try {
      await updateCategoryStatusApi(id, { categortystatus: newStatus });

      const updated = categories.map((cat) =>
        cat.id === id
          ? { ...cat, categortystatus: newStatus } // ✅ enum value
          : cat
      );

      setCategories(updated);
      toast.success(`Status updated successfully`);
    } catch (error) {
      toast.error("Failed to update status!");
    }
  };
  const handleApproveCategory = async (
    categoryId: number,
    value: string
  ) => {

    try {

      if (value === "Approved") {

        const res = await approveCategoryApi(categoryId);

        if (res?.success) {

          toast.success(res.message);

          // ✅ instant update without refresh
          setCategories((prev) =>
            prev.map((cat: any) =>
              cat.id === categoryId
                ? { ...cat, status: "Approved" }
                : cat
            )
          );

          setSelectedCategory((prev: any) =>
            prev
              ? {
                ...prev,
                status: "Approved",
              }
              : null
          );
        }
      }

    } catch (error) {

      console.error(error);

      toast.error("Failed to approve category");
    }
  };

  const confirmDelete = async () => {
    if (deleteId === null) return;
    try {
      await deleteCategoryApi(deleteId);
      setCategories((prev) => prev.filter((cat) => cat.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const filtered = categories.filter((c) =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const paginatedData = filtered
    .slice(startIndex, startIndex + itemsPerPage)
    .map((item, index) => ({
      ...item,
      serial: startIndex + index + 1,
    }));

  const columns = [
    { key: "serial", label: "S.NO" },
    {
      key: "image",
      label: "Image",
      render: (value: string) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            <img
              src={value}
              alt="Category"
              className="category-thumb"
              style={{ cursor: "pointer" }}
            />
          </a>
        ) : (
          <span className="no-thumb">No image</span>
        ),
    },
    {
      key: "metalTypeName",
      label: "Metal Type",
    },
    { key: "name", label: "Category Name" },
    { key: "createdByRole", label: "created By " },
    {
      key: "categortystatus",
      label: "Status",
      render: (value: string, row: any) => (
        <button
          className={`status-pill ${value.toLowerCase()}`}
          onClick={() => toggleStatus(row.id, row.categortystatus)}
        >
          {value}
        </button>
      ),
    },
  ];

  return (
    <div className="category-container">
      <div className="header-bar">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="search-bar"
        />
        <button className="add-btn" onClick={() => setShowAddModal(true)}>
          Add Category
        </button>
      </div>

      <CommonTable
        columns={columns}
        data={paginatedData}
        tableClassName="compact-table"
        actions={(row) => (
          <div className="action-icons">
            <button
              className="icon-btn"
              title="View Category"
              onClick={() => setSelectedCategory(row)}
            >
              <Eye size={16} />
            </button>

            <button
              className="icon-btn"
              title="Edit Category"
              onClick={() => navigate(`/edit/category/${row.id}`)}
            >
              <Pencil size={16} />
            </button>

            <button
              className="icon-btn delete"
              title="Delete Category"
              onClick={() => setDeleteId(row.id)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      />

      <div className="pagination">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="add-category-overlay">
          <div className="add-category-modal">
            <AddCategory
              onClose={() => setShowAddModal(false)}
              onSuccess={() => {
                setShowAddModal(false);
                loadCategories();
              }}
            />
          </div>
        </div>
      )}

      {/* View Modal */}
      {selectedCategory && (
        <div className="modal-overlay" onClick={() => setSelectedCategory(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Category Details</h3>
            {selectedCategory.image && (
              <img
                src={selectedCategory.image}
                alt="Category"
                className="category-thumb large"
                style={{ marginBottom: 12 }}
              />
            )}
            <p><strong>Name:</strong> {selectedCategory.name}</p>
            <div style={{ marginBottom: "15px" }}>
              <strong>Approval Status:</strong>

              <select
                value={selectedCategory.status}
                onChange={(e) =>
                  handleApproveCategory(
                    selectedCategory.id,
                    e.target.value
                  )
                }
                style={{
                  marginLeft: "10px",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              >
                <option value="pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <p><strong>Description:</strong> {selectedCategory.description}</p>
            <button className="add-btn" onClick={() => setSelectedCategory(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId !== null && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this category?</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
              <button className="add-btn delete" onClick={confirmDelete}>
                Yes
              </button>
              <button className="add-btn" onClick={() => setDeleteId(null)}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
